import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import axios, { AxiosRequestConfig } from 'axios';

import { BASE_GO_API_URL, GO_API_URL, NODE_API_URL } from '@/const';
import client from '@/lib/http/client';

export const svGoApiClient = axios.create({
	// withCredentials: true,
	baseURL: GO_API_URL || 'http://localhost:3010/v1',
	timeout: 30000, // 30 seconds timeout,
	// headers: {
	// 	Authorization: `Bearer ${process.env.REACT_APP_TEMP_API_KEY}`,
	// },
});

export const svBaseGoApiClient = axios.create({
	baseURL: BASE_GO_API_URL || 'http://localhost:3010/v1',
	timeout: 30000, // 30 seconds timeout,
});

const svCopilotApiClient = axios.create({
	baseURL: NODE_API_URL || 'http://localhost:3006/v1',
	timeout: 30000, // 30 seconds timeout,
});

svGoApiClient.interceptors.request.use((config) => {
	try {
		const accessToken = localStorage?.getItem('sv_go_key');

		const parsedToken: string | null = accessToken ? accessToken : '';

		if (parsedToken) {
			config!.headers!.Authorization = `Bearer ${parsedToken}`;
		}
	} catch (error) {
		console.log('axios request interceptor: ', error);
	}

	return config;
});

svCopilotApiClient.interceptors.request.use((config) => {
	try {
		const accessToken = localStorage?.getItem('sv_go_key');

		const parsedToken: string | null = accessToken ? accessToken : '';

		if (parsedToken) {
			config!.headers!.Authorization = `Bearer ${parsedToken}`;
		}
	} catch (error) {
		console.log('axios request interceptor: ', error);
	}

	return config;
});

interface IOptions {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
	status?: string;
	search?: string;
}

interface ICopilotVideosOptions extends IOptions {
	onlyUser?: boolean;
	userId?: number;
	favourites?: boolean;
	sortBy?: 'requested_at';
	sortOrder?: 'asc' | 'desc';
	search?: string;
}

interface IBatchProcess {
	collectionName?: string;
	actions: string[];
	tags?: string[];
	urls: string[];
	limit?: number;

	name?: string;
}

interface ICopilotBatch {
	url: string[];
}

interface IFeedbackData {
	feedback: string;
	rate: any;
	user_id: number;
	url: string;
	api_key: string;
}

interface IQACollectionSaveData {
	title: string;
	question: string;
	platform: string;
	brands: string[];
	keywords: string[];

	brandsCondition: 'and' | 'or';
	keywordsCondition: 'and' | 'or';

	reach: string[];
	languages: { code: string; language_name: string }[];

	analysisDateRange: {
		from: string;
		till: string;
	};
	videoDateRange: {
		from: string;
		till: string;
	};
	sentiments: string[];
	favourite: boolean;
}

interface IQACollectionSaveData_ver2 {
	title: string;
	description: string;
	favourite: boolean;

	prompts: {
		title: string;
		prompt: string;
	}[];
}

interface IQACollectionUpdateFavouriteData {
	id: string;
	favourite: boolean;
}

interface IQACollectionUpdateData extends IQACollectionSaveData_ver2 {
	id: string;
}

export interface CopilotHistoryResponse {
	data: any;
}

export interface CopilotBatchResponse {
	data: {
		item: CopilotHistoryBatchItem[];
		pagination: {
			total: number;
			page: number;
			limit: number;
		};
	};
}
export interface CopilotHistory {
	results: CopilotHistoryPageItem[];
	total: number;
}

export interface CopilotHistoryPageItem {
	id: number;
	url: string;
	platform: string;
	title: string;
	description: string;
	thumbnail: string;
	status: number;
	createdAt: Date;
	favourite: boolean;

	creatorUrls: string[];
	creatorName: string | null;
}

export interface CopilotHistoryBatchItem {
	batch_id: string;
	history_count: number;
	mongo_ids: string[];
	created_at: Date;
	videos: {
		mongo_id: string;
		status: number | null;
	}[];
}

function getAllProcesses({
	page = 1,
	limit = 10,
	sortBy,
	sortOrder,
	status,
	search = '',
}: IOptions) {
	// return svGoApiClient.get<any>(
	let filterStatus = '',
		searchQuery = '';

	if (status) {
		filterStatus = `&status=${status}`;
	}

	if (search) {
		searchQuery = `&search=${encodeURIComponent(search)}`;
	}

	return client.get<any>(
		`/reports/process?page=${page}&limit=${limit}&type=urls&sortBy=${sortBy}&sortOrder=${sortOrder}${filterStatus}${searchQuery}`,
	);
}

function archiveProcess(id: string) {
	return client.put<any>(`/reports/process/${id}`);
}

function getSingleReportQualityStats(id: string) {
	return client.get<any>(`/reports/data/${id}/quality-stats`);
}

function getAllCopilotVideos({
	page = 1,
	limit = 10,
	onlyUser = false,
	sortBy,
	sortOrder,
	favourites = false,
	search = '',
}: ICopilotVideosOptions) {
	const queryParams = `page=${page}&limit=${limit}&sortOrder=${sortOrder}&sort=${sortBy}&onlyUser=${onlyUser}&favourites=${favourites}&search=${search}`;
	// /copilot/history
	return client.get<CopilotHistoryResponse>(`/copilot/history?${queryParams}`);
}

function getCopilotBatches({
	page = 1,
	limit = 10,
	sortBy,
	sortOrder,
}: ICopilotVideosOptions) {
	const queryParams = `page=${page}&limit=${limit}&sortOrder=${sortOrder}&sort=${sortBy}`;
	// /copilot/history
	return client.get<CopilotHistoryResponse>(`/copilot/batch?${queryParams}`);
}

function getSingleCopilotVideo(url: string) {
	return svGoApiClient.get<CopilotHistoryResponse>(`/copilot?url=${url}`);
}

function getNewApiKey() {
	return svGoApiClient.get<any>(`/gen-api-key`);
}

function getProcessesById(id: string) {
	return svGoApiClient.get<any>(`/process/${id}`);
}

function createBatchProcess(batchProcess: IBatchProcess) {
	return svGoApiClient.post<any>(`/video`, batchProcess as AxiosRequestConfig);
}

function createCopilotBatch(batchProcess: ICopilotBatch) {
	return svGoApiClient.post<any>(
		`/copilot/queue/batch`,
		batchProcess as any as AxiosRequestConfig,
	);
}

function reprocessBatchProcess(id: string) {
	return svGoApiClient.post<any>(`/video/${id}/reprocess`);
}

function deleteBatchProcess(id: string) {
	return client.delete<any>(`/reports/${id}`);
}

function sendFeedback({
	feedback,
	rate,
	user_id,
	url,
	api_key,
}: IFeedbackData) {
	return svGoApiClient.post<any>(
		`/copilot/review`,
		{
			comment: feedback,
			vote: rate,
			user_id,
			content_url: url ?? null,
		} as AxiosRequestConfig,
		{
			headers: {
				Authorization: `Bearer ${api_key}`,
			},
		},
	);
}

function getReportIdByPlatformHandle(platformHandle: string) {
	return svGoApiClient.get<any>(
		`/reports/id/byPlatformHandle?q=${platformHandle}`,
	);
}

export function useGetReportIdByPlatformHandle(
	platformHandle: string,
	enabled = false,
) {
	return useQuery(
		['reportId', platformHandle],
		() => getReportIdByPlatformHandle(platformHandle),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
			enabled,
		},
	);
}

export async function downloadBatchJsonData(id: string) {
	return await svGoApiClient.get(`/process/${id}/content/json`, {
		responseType: 'blob',
	});
}

export function useGetAllCustomerProcesses(
	{ page = 1, limit = 10, sortBy, sortOrder, status, search = '' }: IOptions,
	isRefreshEnabled: boolean = false,
) {
	return useQuery(
		['processes', page, limit, sortBy, sortOrder, status, search],
		() =>
			getAllProcesses({
				page,
				limit,
				sortBy,
				sortOrder,
				status,
				search,
			}),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
			refetchInterval: isRefreshEnabled ? 1000 * 10 : false,
		},
	);
}

export function useGetAllCopilotVideos(
	options: ICopilotVideosOptions,
	isRefreshEnabled: boolean = false,
) {
	const { page, limit, favourites, onlyUser, sortBy, sortOrder, search } =
		options;
	return useQuery(
		[
			'copilot-videos',
			page,
			limit,
			sortBy,
			sortOrder,
			onlyUser ? '3' : '4',
			favourites ? '1' : '2',
			search ? search : '',
		],
		() =>
			getAllCopilotVideos({
				page,
				limit,
				sortBy,
				sortOrder,
				onlyUser: onlyUser ? true : false,
				favourites,
				search: search?.trim(),
			}),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
			// refetchInterval: isRefreshEnabled ? 1000 * 10 : false,
		},
	);
}

export function useGetCopilotBatches(
	options: ICopilotVideosOptions,
	isAutoRefreshOn: boolean = false,
) {
	const { page, limit, sortBy, sortOrder } = options;
	return useQuery(
		['copilot-batch', page, limit, sortBy, sortOrder],
		() =>
			getCopilotBatches({
				page,
				limit,
				sortBy,
				sortOrder,
			}),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
			refetchInterval: isAutoRefreshOn && 1000 * 10,
		},
	);
}

export function useGetSingleCopilotVideo(
	url: string,
	enabled: boolean = false,
) {
	return useQuery(['copilot-video', url], () => getSingleCopilotVideo(url), {
		keepPreviousData: false,
		refetchOnWindowFocus: false,
		staleTime: 1000 * 60 * 5,
		enabled,
	});
}

export function useGetBatchProcessesById(id: string) {
	return useQuery(['processes', id], () => getProcessesById(id), {
		keepPreviousData: true,
		refetchOnWindowFocus: false,
		staleTime: 1000 * 60 * 5,
		enabled: typeof id !== 'undefined',
	});
}

export function useCreateBatchProcess() {
	const queryClient = useQueryClient();

	return useMutation(createBatchProcess, {
		onSuccess: () => {
			queryClient.invalidateQueries();
		},
	});
}

export function useCreateCopilotBatch() {
	const queryClient = useQueryClient();

	return useMutation(createCopilotBatch, {
		onSuccess: () => {
			queryClient.invalidateQueries(['copilot-batch']);
		},
	});
}

export function useArchiveProcess() {
	const queryClient = useQueryClient();

	return useMutation(archiveProcess, {
		onSuccess: () => {
			queryClient.invalidateQueries(['processes']);
		},
	});
}

export function useReprocessBatchProcess() {
	const queryClient = useQueryClient();

	return useMutation(reprocessBatchProcess, {
		onSuccess: () => {
			queryClient.invalidateQueries(['processes']);
		},
	});
}

export function useDeleteBatchProcess() {
	const queryClient = useQueryClient();

	return useMutation(deleteBatchProcess, {
		onSuccess: () => {
			queryClient.invalidateQueries(['processes']);
		},
	});
}

export function useSendFeedback() {
	return useMutation(sendFeedback);
}

export function useDownloadBatchProcessJson(id: string) {
	return useQuery(['batchJsonData', id], () => downloadBatchJsonData(id), {
		keepPreviousData: true,
		refetchOnWindowFocus: false,
		enabled: typeof id !== 'undefined',
	});
}

export function useGetNewApiKey() {
	return useQuery(['api-key'], () => getNewApiKey(), {
		refetchOnWindowFocus: false,
		enabled: false,
	});
}

function fetchShareItem(options: { userId: number; url: string }) {
	return svGoApiClient.post<any>(`/copilot/share`, {
		// userId: options?.userId,
		url: options?.url,
	});
}

export function useShareAnalysis() {
	return useMutation(fetchShareItem);
}

function updateFavorites(options: { id: string; isAdd: boolean }) {
	return svGoApiClient.post<any>(`/copilot/history/like/${options.id}`, {
		isFavourite: options.isAdd,
	} as AxiosRequestConfig);
}

export function useAddRemoveFromFavorites() {
	const queryClient = useQueryClient();

	return useMutation(updateFavorites, {
		retry: 3,
		retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 3000),
		onSuccess: () => {
			queryClient.invalidateQueries(['copilot-videos']);
			queryClient.invalidateQueries(['single-videos-collections']);
			queryClient.invalidateQueries(['videos-collections-with-ids']);
			message.success('Updated');

			// setTimeout(() => {
			// 	queryClient.invalidateQueries([
			// 		'copilot-videos',
			// 		'single-videos-collections',
			// 	]);
			// }, 500);
		},
	});
}

function getQuestionsHistory({
	page,
	limit,
	url,
}: // userId,
{
	page: number;
	limit: number;
	url: string;
	// userId: number;
}) {
	return svGoApiClient.get<any>(
		`/copilot/qa?videoURL=${url}&page=${page ?? 1}&limit=${limit ?? 10}`,
	);
}

export function useGetQuestionsHistory({
	page = 1,
	limit = 10,
	// userId,
	url,
	enabled = false,
}: any) {
	return useQuery(
		['questions-history', page, limit, url],
		() =>
			getQuestionsHistory({
				page,
				limit,
				// userId,
				url,
			}),
		{
			keepPreviousData: true,
			staleTime: 1000 * 60,
			refetchOnWindowFocus: false,
			retry: 3,
			enabled,
			retryDelay: 3000,
		},
	);
}

function askQuestion({
	url,
	question,
}: // userId,
{
	url: string;
	question: string;
	// userId: number;
}) {
	return svGoApiClient.post<any>(`/copilot/qa`, {
		url,
		question,
		// userId,
	} as AxiosRequestConfig);
}

export function useAskQuestion() {
	const queryClient = useQueryClient();

	return useMutation(askQuestion, {
		onSuccess: () => {
			queryClient.invalidateQueries(['questions-history']);
		},
	});
}

function getAllQAData({
	page,
	limit,
	onlyUser = false,
}: // userId,
{
	page: number;
	limit: number;
	onlyUser?: boolean;
	// userId: number;
}) {
	const onlyUserStr = onlyUser ? '&onlyUser=true' : '';

	return client.get<any>(
		`/copilot/qa?page=${page ?? 1}&limit=${limit ?? 10}${onlyUserStr}`,
	);
}

export function useGetAllQAData({
	page = 1,
	limit = 10,
	// userId,
	onlyUser,
	enabled = false,
}: any) {
	return useQuery(
		['all-qa', page, limit, onlyUser],
		() =>
			getAllQAData({
				page,
				limit,
				// userId,
				onlyUser,
			}),
		{
			keepPreviousData: true,
			staleTime: 1000 * 60,
			refetchOnWindowFocus: false,
			retry: 3,
			enabled,
			retryDelay: 3000,
		},
	);
}

function getAllQACollections({
	page,
	limit,
	onlyUser,
	favourites,
	search,
	sortOrder,
}: // userId,
{
	page: number;
	limit: number;
	onlyUser?: boolean;
	favourites?: boolean;
	search?: string;
	sortOrder?: 'asc' | 'desc';
	// userId: number;
}) {
	return client.get<any>(
		`/copilot/qa-collections?page=${page ?? 1}&limit=${limit ?? 100}&onlyUser=${
			onlyUser ? 'true' : 'false'
		}&favourites=${favourites ? 'true' : 'false'}${
			search ? `&search=${search}` : ''
		}
		&sortOrder=${sortOrder ?? 'desc'}`,
	);
}

export function useGetAllQACollections({
	page = 1,
	limit = 10,
	enabled = false,
	onlyUser = false,
	favourites = false,
	sortOrder,
	search = '',
}: any) {
	return useQuery(
		['qa-collections', page, limit, onlyUser, favourites, search, sortOrder],
		() =>
			getAllQACollections({
				page,
				limit,
				onlyUser,
				favourites,
				search,
				sortOrder,
			}),
		{
			keepPreviousData: true,
			staleTime: 1000 * 60,
			refetchOnWindowFocus: false,
			retry: 3,
			enabled,
			retryDelay: 3000,
		},
	);
}

function getLanguages() {
	return client.get<any>(`/copilot/languages`);
}

export function useGetLanguages(enabled: boolean = false) {
	return useQuery(['languages'], getLanguages, {
		keepPreviousData: true,
		staleTime: 1000 * 60 * 5,
		refetchOnWindowFocus: false,
		enabled,
	});
}

function saveQACollectionData(data: IQACollectionSaveData_ver2) {
	return client.post<any>(
		`/copilot/qa-collections`,
		data as AxiosRequestConfig,
	);
}

export function useSaveQACollectionData() {
	const queryClient = useQueryClient();

	return useMutation(saveQACollectionData, {
		onSuccess: () => {
			// Invalidate queries to refresh data
			queryClient.invalidateQueries(['qa-collections']);
		},
	});
}

function updateQACollectionFavourite(data: IQACollectionUpdateFavouriteData) {
	return client.put<any>(`/copilot/qa-collections/${data?.id}/favourite`, {
		favourite: (data as any)?.favourite,
	} as AxiosRequestConfig);
}

export function useUpdateQACollectionFavourite() {
	const queryClient = useQueryClient();

	return useMutation(updateQACollectionFavourite, {
		onSuccess: () => {
			// Invalidate queries to refresh data
			queryClient.invalidateQueries(['qa-collections']);
		},
	});
}

function updateQACollection(data: IQACollectionUpdateData) {
	const { id, ...rest } = data;

	return client.put<any>(`/copilot/qa-collections/${data?.id}`, {
		...rest,
	} as AxiosRequestConfig);
}

export function useUpdateQACollection() {
	const queryClient = useQueryClient();

	return useMutation(updateQACollection, {
		onSuccess: () => {
			queryClient.invalidateQueries(['qa-collections']);
			queryClient.invalidateQueries(['single-qa-collection']);
		},
	});
}

function deleteQACollection(id: string) {
	return client.delete<any>(`/copilot/qa-collections/${id}`);
}

export function useDeleteQACollection() {
	const queryClient = useQueryClient();

	return useMutation(deleteQACollection, {
		onSuccess: () => {
			// Invalidate queries to refresh data
			queryClient.invalidateQueries(['qa-collections']);
		},
	});
}

export function useArchivePost() {
	const queryClient = useQueryClient();

	return useMutation(archivePost, {
		onSuccess: () => {
			setTimeout(() => {
				queryClient.invalidateQueries(['copilot-videos']);
			}, 100);
		},
	});
}

export function useDeleteBatch() {
	const queryClient = useQueryClient();

	return useMutation(deleteBatch, {
		onSuccess: () => {
			setTimeout(() => {
				queryClient.invalidateQueries(['copilot-batch']);
			}, 100);
		},
	});
}

export function useGetSingleReportBatchQualityStats(
	id: string,
	enabled = false,
) {
	return useQuery(
		[`quality-stats`, id],
		() => getSingleReportQualityStats(id),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
			retry: 3,
			enabled: !!id && enabled,
		},
	);
}

function archivePost(id: number) {
	return client.post<any>(`/copilot/archive-ui`, {
		id,
	} as AxiosRequestConfig);
}

function deleteBatch(id: string) {
	return client.delete<any>(`/copilot/delete-batch/${id}`);
}
